import React from 'react'
import {IbeConfig, OfferResult, PropertyConfig} from '../../types'
import SearchUnitResultItem from './SearchUnitResultItem'
import {findPropertyConfig} from '../../services/utils'
import {getUnitGroupOffers} from '../../services/offerUtils'
import {getLogger} from '../../services/logging'
import {useAppSelector} from '../../store/hooks'
import {selectConfig, selectPropertyById} from '../../store/slices/configSlice'
import EmptyResults from '../../assets/ibe-empty-results.png'
import EmptyUnavailable from '../../assets/ibe-empty-unavailable.png'
import EmptyOpening from '../../assets/ibe-empty-opening.png'
import {selectSearchParams} from '../../store/slices/searchParamsSlice'
import {
  formatDisplayDate,
  isAfterDay,
  isBeforeDay,
  toPropertyDateTime
} from '../../services/dateUtils'

const logger = getLogger('SearchUnitResults')

interface PropertyStatus {
  status: 'not_opened' | 'closed' | 'open'
  openingDate?: string
  closingDate?: string
}

/**
 * Checks the property's opening status based on search date range
 * @param propertyConfig The property configuration
 * @param dateRange The search date range
 * @returns object containing the status and relevant dates
 */
function checkPropertyStatus(
  propertyConfig: PropertyConfig,
  dateRange?: {
    type: 'Specific' | 'Flexible'
    start?: string
    end?: string
    nights?: number
  }
): PropertyStatus {
  const openingDate = propertyConfig?.openingDate
  const closingDate = propertyConfig?.closingDate
  const startDate = dateRange?.start
  const endDate = dateRange?.end

  // search start date is before property is open
  if (startDate && openingDate && isBeforeDay(startDate, openingDate)) {
    return {
      status: 'not_opened',
      openingDate
    }
  }

  // search end date is after property is closed
  if (endDate && closingDate && isAfterDay(endDate, closingDate)) {
    return {
      status: 'closed',
      closingDate
    }
  }

  // default to open
  return {status: 'open'}
}

/**
 * Formats the property selection label based on the selected property ID
 * @param selectedPropertyId
 * @param config
 * @returns
 */
function formatPropertySelectionLabel(
  selectedPropertyId: string,
  config: IbeConfig | null
): string {
  if (selectedPropertyId === 'ALL') {
    return 'All Properties'
  } else if (!config) {
    return 'Property'
  } else {
    const propertyConfig = findPropertyConfig(
      config.properties,
      selectedPropertyId
    )
    return propertyConfig?.name || 'Selected Property'
  }
}

export interface SearchUnitResultsProps {
  offerResults?: OfferResult[]
  selectedPropertyId: string
  isLoading: boolean
}

const SearchUnitResults = ({
  offerResults = [],
  selectedPropertyId,
  isLoading
}: SearchUnitResultsProps) => {
  const config = useAppSelector(selectConfig)
  const propertyConfig = useAppSelector(
    selectPropertyById(selectedPropertyId)
  ) as PropertyConfig
  const searchParams = useAppSelector(selectSearchParams)
  const propertyStatus: PropertyStatus = checkPropertyStatus(
    propertyConfig,
    searchParams.dateRange
  )
  const propertySelectionLabel = config
    ? formatPropertySelectionLabel(selectedPropertyId, config)
    : 'Property'
  const unitGroupOffers = getUnitGroupOffers(selectedPropertyId, offerResults)

  // get special rate plans for selected property (if special rates are enabled)
  const {dateRange, guests, specialRates} = searchParams

  logger.debug({
    message: 'Rendering SearchUnitResults...',
    data: {unitGroupOffers}
  })

  // property is not open or is closed
  if (propertyStatus.status !== 'open') {
    if (propertyStatus.status === 'not_opened') {
      return (
        <div className="text-center p-8 mb-12">
          <img
            src={EmptyOpening}
            className="max-h-72 mx-auto"
            alt="Property opening soon image."
          />
          <h2 className="text-[1.5rem] leading-8 font-bold text-[#122634] mb-2 mt-8">
            The Countdown Is On!
          </h2>
          <p className="text-base leading-[22px] text-[#122634] mb-4 max-w-[700px] mx-auto">
            You're in the right spot—but it's not ready for stays just yet.{' '}
            <span className="font-bold">{propertyConfig?.name}</span>{' '}
            <span className="font-bold">begins welcoming guests on</span>{' '}
            <span className="font-bold">
              {formatDisplayDate(
                toPropertyDateTime(
                  propertyStatus.openingDate,
                  propertyConfig.timeZone
                )
              )}
            </span>
            . Try adjusting your dates or explore another Placemakr
            property—we'd love to host you.
          </p>
          <button
            onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
            className="btn btn-secondary md:hidden mt-4"
          >
            Search again
          </button>
        </div>
      )
    } else {
      return (
        <div className="text-center p-8 mb-12">
          <img
            src={EmptyUnavailable}
            className="max-h-72 mx-auto"
            alt="Property is closed image."
          />
          <h2 className="text-[1.5rem] leading-8 font-bold text-[#122634] mb-2 mt-8">
            We're Wrapping Things Up Here
          </h2>
          <p className="text-base leading-[22px] text-[#122634] mb-4 max-w-[700px] mx-auto">
            Your selected dates fall after{' '}
            <span className="font-bold">{propertyConfig?.name}</span> has closed
            its reservation books. We're no longer accepting bookings after{' '}
            <span className="font-bold">
              {formatDisplayDate(
                toPropertyDateTime(
                  propertyStatus.closingDate,
                  propertyConfig.timeZone
                )
              )}
            </span>
            —but the good news? We've got other great stays nearby. Try
            adjusting your dates or explore another location.
          </p>
          <button
            onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
            className="btn btn-secondary md:hidden mt-4"
          >
            Search again
          </button>
        </div>
      )
    }
  }

  if (isLoading) {
    // loading
    return (
      <div className="flex-1 flex flex-col gap-8 mt-8">
        <div className="skeleton h-64 w-full"></div>
        <div className="skeleton h-64 w-full"></div>
        <div className="skeleton h-64 w-full"></div>
      </div>
    )
  } else if (!isLoading && unitGroupOffers?.length) {
    // show unit results
    return (
      <>
        <div className="text-sm mt-4">
          Showing <span className="font-bold">{unitGroupOffers.length}</span>{' '}
          room results for{' '}
          <span className="font-bold">{propertySelectionLabel}</span>:
        </div>
        <div className="flex flex-col gap-8 mt-4 mb-8">
          {unitGroupOffers.map((unitGroupOffer, index) => {
            return (
              <SearchUnitResultItem
                key={index}
                unitGroupOffer={unitGroupOffer}
                propertyId={unitGroupOffer.propertyId}
              />
            )
          })}
        </div>
      </>
    )
  } else {
    // no results
    return (
      <div className="text-center p-8 mb-12">
        <img
          src={EmptyResults}
          className="max-h-72 mx-auto"
          alt="No results found image."
        />
        <h2 className="text-[1.5rem] leading-8 font-bold text-[#122634] mb-2 mt-8">
          No matches this time.
        </h2>
        <p className="text-base leading-[22px] text-[#122634] mb-4 mx-auto">
          Try tweaking your filters or choosing new travel dates—we'll help you
          find a place that fits.
        </p>

        <button
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          className="btn btn-secondary md:hidden mt-4"
        >
          Search again
        </button>
      </div>
    )
  }
}

export default SearchUnitResults
